import type { StorageHook } from '../../hooks/use-snooze-state';
import type { ServiceCollection } from '@shipstation/client-common/types/seller-data';
import { RATE_SHOPPER_POPUP_DISMISSED_AT, useUserSpecificLocalStorage } from '../../hooks/use-local-storage';
import { useSnoozeState } from '../../hooks/use-snooze-state';
import { useEnableDefaultRateShopperPopup } from '../../feature-flags/hooks/use-enable-default-rate-shopper-popup';
import { useSelector } from 'react-redux';
import Global from '../../store/global-selectors';
import { UserPermissionService } from '../../services/user-permission-service';
import { CHEAPEST_SERVICE_COLLECTION_GUID } from '@shipstation/client-common/constants/service-collections';

const SNOOZE_DURATION_MS = 24 * 60 * 60 * 1000;
const MAX_DISMISSAL_COUNT = 3;

export const useUserHasShippingConfigurationRole = (): boolean => {
  const userRoles = useSelector(Global.selectUseruserRoles);
  return UserPermissionService.userHasConfigurationRole(userRoles);
};

export const useRateShopperPopupDismissalState = () => {
  const { isSnoozed, snooze, snoozePermanently } = useSnoozeState({
    storageKey: RATE_SHOPPER_POPUP_DISMISSED_AT,
    useStorage: useUserSpecificLocalStorage as StorageHook,
    snoozeDurationMs: SNOOZE_DURATION_MS,
    maxSnoozeCount: MAX_DISMISSAL_COUNT,
  });

  return { isDismissed: isSnoozed, snooze, snoozePermanently };
};

const useSellerHasRateShopper = () => {
  const rateShoppers = useSelector(Global.selectSellerServiceCollections) ?? [];
  return rateShoppers.some((rateShopper: ServiceCollection) => {
    return rateShopper.serviceCollectionGuid === CHEAPEST_SERVICE_COLLECTION_GUID;
  });
};

export const useShouldShowRateShopperPopup = (): boolean => {
  const { isDismissed } = useRateShopperPopupDismissalState();
  const enableDefaultRateShopperPopup = useEnableDefaultRateShopperPopup();
  const sellerHasRateShopper = useSellerHasRateShopper();
  const userHasShippingConfigurationRole = useUserHasShippingConfigurationRole();

  if (!enableDefaultRateShopperPopup) return false;
  if (!userHasShippingConfigurationRole) return false;
  if (!sellerHasRateShopper) return false;

  return !isDismissed;
};
